import {
    QueryClient,
    useIsMutating,
    useMutation,
    useQuery,
    useQueryClient,
} from "@tanstack/react-query";
import { ServerInferRequest } from "@ts-rest/core";
import {
    BankReconciliation,
    reconciliationContract,
    ReconciliationErrorCodes,
} from "@common/contracts/reconciliation.contract";
import { getLastBankReconciliationForAccount } from "@common/helpers/reconciliation";
import { FinancialAccount } from "@common/types/financialAccount";
import { useScope } from "@state/scopeContext.lib";
import { getEntitiesAccountsQueryKey } from "@queries/entitiesAccounts";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { SUPPRESS_ERROR_TOAST_HEADER } from "@lib/backendClient";
import { isAxiosError } from "axios";
import { financialDocumentQueryKeys } from "@components/documents/lib";
import { getTransactionsStatistics } from "./transaction.api";

export const reconciliationContractClient = getBackendAPIClient(
    reconciliationContract,
);

const reconciliationQueryKeys = {
    all: ["bank-reconciliation"] as const,
    lists: () => [...reconciliationQueryKeys.all, "list"] as const,
    summary: (financialAccountId: number) =>
        [
            ...reconciliationQueryKeys.all,
            "summary",
            financialAccountId,
        ] as const,
    bankReconciliations: (entityIds: number[]) =>
        [
            ...reconciliationQueryKeys.lists(),
            [...entityIds].sort((a, b) => a - b),
        ] as const,
    allOutstandingCashItems: () =>
        [...reconciliationQueryKeys.all, "outstanding-cash-items"] as const,
    outstandingCashItems: (financialAccountId: number) =>
        [
            ...reconciliationQueryKeys.allOutstandingCashItems(),
            financialAccountId,
        ] as const,
    undoLockBlockers: (entityId: number, financialAccountId: number) =>
        [
            ...reconciliationQueryKeys.all,
            "undo-lock-blockers",
            entityId,
            financialAccountId,
        ] as const,
};

const reconciliationMutationKey = [
    ...reconciliationQueryKeys.all,
    "mutation",
] as const;

export const undoReconciliationLockBlockersQueryKey = [
    ...reconciliationQueryKeys.all,
    "undo-lock-blockers",
] as const;

export type ReconcileTransactionsPayload = Omit<
    ServerInferRequest<
        typeof reconciliationContract.reconcileTransactions
    >["body"],
    "workspaceId"
>;

export function refreshDocumentsWhenPeriodReconciled(queryClient: QueryClient) {
    return async (error: Error) => {
        if (
            isAxiosError(error) &&
            error.response?.status === 400 &&
            error.response.data?.errorCode ===
                ReconciliationErrorCodes.STATEMENT_PERIOD_ALREADY_RECONCILED
        ) {
            await queryClient.invalidateQueries({
                queryKey: financialDocumentQueryKeys.details(),
            });
        }
    };
}

export function useReconcileTransactionsMutation(onSuccess?: () => void) {
    const { workspaceId } = useScope();
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: [...reconciliationMutationKey, "reconcile"],
        mutationFn: async (payload: ReconcileTransactionsPayload) => {
            await reconciliationContractClient.reconcileTransactions({
                body: {
                    ...payload,
                    workspaceId: workspaceId ?? "",
                },
            });
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: getEntitiesAccountsQueryKey(workspaceId ?? ""),
            });

            await queryClient.invalidateQueries({
                queryKey: reconciliationQueryKeys.lists(),
            });
            await queryClient.invalidateQueries({
                queryKey: reconciliationQueryKeys.allOutstandingCashItems(),
            });
            onSuccess?.();
        },
        onError: refreshDocumentsWhenPeriodReconciled(queryClient),
    });
}

export function useIsReconciliationSaving(): boolean {
    return useIsMutating({ mutationKey: reconciliationMutationKey }) > 0;
}

interface UseHasOutstandingCashItemsParams {
    financialAccount: FinancialAccount;
    enabled: boolean;
}

export function useHasOutstandingCashItems({
    financialAccount,
    enabled,
}: UseHasOutstandingCashItemsParams) {
    const { workspaceId } = useScope();
    const entityId = financialAccount.entityId;

    return useQuery({
        queryKey: reconciliationQueryKeys.outstandingCashItems(
            financialAccount.id,
        ),
        queryFn: async () => {
            const statistics = await getTransactionsStatistics(
                {
                    entityIds: entityId ? [entityId] : undefined,
                    accountIds: [financialAccount.id],
                    isUnreconciledCheck: true,
                    includeBeforeBookkeepingStart: true,
                },
                workspaceId ?? "",
            );
            return statistics.count > 0;
        },
        enabled: enabled && !!workspaceId && !!entityId,
    });
}

interface UseBankReconciliationsOptions {
    enabled?: boolean;
}

export function useBankReconciliations(
    entityIds: number[],
    options?: UseBankReconciliationsOptions,
) {
    const { workspaceId } = useScope();

    return useQuery({
        queryKey: reconciliationQueryKeys.bankReconciliations(entityIds),
        queryFn: async () => {
            const response =
                await reconciliationContractClient.getBankReconciliations({
                    params: { workspaceId: workspaceId ?? "" },
                    query: { entityIds },
                });

            return response.body;
        },
        enabled:
            (options?.enabled ?? true) && !!workspaceId && entityIds.length > 0,
    });
}

interface LastReconciliationResult {
    lastReconciliation: BankReconciliation | undefined;
    isLoading: boolean;
}

export function useLastReconciliationForAccount(
    financialAccountId: number | undefined,
    entityId: number | null | undefined,
): LastReconciliationResult {
    const { data: reconciliations, isLoading } = useBankReconciliations(
        entityId ? [entityId] : [],
        { enabled: !!entityId },
    );

    if (!financialAccountId || isLoading || !reconciliations) {
        return { lastReconciliation: undefined, isLoading };
    }

    return {
        lastReconciliation: getLastBankReconciliationForAccount(
            reconciliations,
            financialAccountId,
        ),
        isLoading: false,
    };
}

export function useUndoReconciliationLockBlockers({
    entityId,
    financialAccountId,
    enabled,
}: {
    entityId: number;
    financialAccountId: number;
    enabled: boolean;
}) {
    return useQuery({
        queryKey: reconciliationQueryKeys.undoLockBlockers(
            entityId,
            financialAccountId,
        ),
        queryFn: async () => {
            const response =
                await reconciliationContractClient.getUndoReconciliationLockBlockers(
                    {
                        params: { entityId, financialAccountId },
                        extraHeaders: {
                            [SUPPRESS_ERROR_TOAST_HEADER]: "true",
                        },
                    },
                );

            return response.body;
        },
        enabled,
    });
}

export function useLastBankReconciliationRemovalMutation(
    params: ServerInferRequest<
        typeof reconciliationContract.deleteLastBankReconciliation
    >["params"],
    onSuccess?: () => void,
    onError?: (error: unknown) => void,
) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async () => {
            const response =
                await reconciliationContractClient.deleteLastBankReconciliation(
                    {
                        params,
                        extraHeaders: {
                            [SUPPRESS_ERROR_TOAST_HEADER]: "true",
                        },
                    },
                );
            return response.body;
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: reconciliationQueryKeys.lists(),
            });
            onSuccess?.();
        },
        onError,
    });
}
